import { Temporal } from '@js-temporal/polyfill';

// ── Date/Time formatting ────────────────────────────────────────────

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const WEEKDAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function pad(value: number, length: number): string {
    return String(value).padStart(length, '0');
}

function hour24to12(hour: number): number {
    const result = hour % 12;
    return result === 0 ? 12 : result;
}

function dayOfWeek(year: number, month: number, day: number): number {
    return Temporal.PlainDate.from({ year, month, day }).dayOfWeek;
}

interface TemporalFields {
    year?: number;
    month?: number;
    day?: number;
    hour?: number;
    minute?: number;
    second?: number;
    millisecond?: number;
    timeZone?: string;
    offset?: string;
}

function extractFields(value: Record<string, unknown>): TemporalFields {
    return {
        year: value.year as number | undefined,
        month: value.month as number | undefined,
        day: value.day as number | undefined,
        hour: value.hour as number | undefined,
        minute: value.minute as number | undefined,
        second: value.second as number | undefined,
        millisecond: value.millisecond as number | undefined,
        timeZone: value.timeZone as string | undefined,
        offset: value.offset as string | undefined,
    };
}

// Tokens sorted longest-first so greedy matching works correctly
const DATE_TIME_TOKENS: Array<{
    token: string;
    resolve: (fields: TemporalFields) => string;
}> = [
    {
        token: 'yyyy',
        resolve: fields => (fields.year !== undefined ? pad(fields.year, 4) : ''),
    },
    {
        token: 'yy',
        resolve: fields => (fields.year !== undefined ? pad(fields.year % 100, 2) : ''),
    },
    {
        token: 'MMMM',
        resolve: fields => (fields.month !== undefined ? MONTH_NAMES[fields.month - 1] : ''),
    },
    {
        token: 'MMM',
        resolve: fields => (fields.month !== undefined ? MONTH_NAMES_SHORT[fields.month - 1] : ''),
    },
    {
        token: 'MM',
        resolve: fields => (fields.month !== undefined ? pad(fields.month, 2) : ''),
    },
    {
        token: 'M',
        resolve: fields => (fields.month !== undefined ? String(fields.month) : ''),
    },
    {
        token: 'dd',
        resolve: fields => (fields.day !== undefined ? pad(fields.day, 2) : ''),
    },
    {
        token: 'd',
        resolve: fields => (fields.day !== undefined ? String(fields.day) : ''),
    },
    {
        token: 'EEEE',
        resolve: fields => {
            if (fields.year === undefined || fields.month === undefined || fields.day === undefined) return '';
            return WEEKDAY_NAMES[dayOfWeek(fields.year, fields.month, fields.day) - 1];
        },
    },
    {
        token: 'EEE',
        resolve: fields => {
            if (fields.year === undefined || fields.month === undefined || fields.day === undefined) return '';
            return WEEKDAY_NAMES_SHORT[dayOfWeek(fields.year, fields.month, fields.day) - 1];
        },
    },
    {
        token: 'HH',
        resolve: fields => (fields.hour !== undefined ? pad(fields.hour, 2) : ''),
    },
    {
        token: 'H',
        resolve: fields => (fields.hour !== undefined ? String(fields.hour) : ''),
    },
    {
        token: 'hh',
        resolve: fields => (fields.hour !== undefined ? pad(hour24to12(fields.hour), 2) : ''),
    },
    {
        token: 'h',
        resolve: fields => (fields.hour !== undefined ? String(hour24to12(fields.hour)) : ''),
    },
    {
        token: 'mm',
        resolve: fields => (fields.minute !== undefined ? pad(fields.minute, 2) : ''),
    },
    {
        token: 'm',
        resolve: fields => (fields.minute !== undefined ? String(fields.minute) : ''),
    },
    {
        token: 'ss',
        resolve: fields => (fields.second !== undefined ? pad(fields.second, 2) : ''),
    },
    {
        token: 's',
        resolve: fields => (fields.second !== undefined ? String(fields.second) : ''),
    },
    {
        token: 'SSS',
        resolve: fields => (fields.millisecond !== undefined ? pad(fields.millisecond, 3) : ''),
    },
    {
        token: 'a',
        resolve: fields => (fields.hour !== undefined ? (fields.hour < 12 ? 'AM' : 'PM') : ''),
    },
    {
        token: 'Z',
        resolve: fields => fields.offset ?? '',
    },
    {
        token: 'z',
        resolve: fields => fields.timeZone ?? '',
    },
];

/**
 * Formats a temporal plain object using a date-fns-style pattern.
 * Literal text can be escaped with single quotes: `'at' HH:mm` -> `at 14:30`
 */
export function formatTemporalPattern(value: Record<string, unknown>, pattern: string): string {
    const fields = extractFields(value);
    let result = '';
    let index = 0;

    while (index < pattern.length) {
        // Handle escaped literal text
        if (pattern[index] === "'") {
            const closingQuote = pattern.indexOf("'", index + 1);
            if (closingQuote === -1) {
                // No closing quote — treat rest as literal
                result += pattern.slice(index + 1);
                break;
            }
            result += pattern.slice(index + 1, closingQuote);
            index = closingQuote + 1;
            continue;
        }

        // Try to match a token (longest first)
        let matched = false;
        for (const { token, resolve } of DATE_TIME_TOKENS) {
            if (pattern.startsWith(token, index)) {
                result += resolve(fields);
                index += token.length;
                matched = true;
                break;
            }
        }

        if (!matched) {
            result += pattern[index];
            index++;
        }
    }

    return result;
}

// ── Duration formatting ─────────────────────────────────────────────

interface DurationFields {
    years: number;
    months: number;
    weeks: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
}

function extractDurationFields(value: Record<string, unknown>): DurationFields {
    return {
        years: (value.years as number) ?? 0,
        months: (value.months as number) ?? 0,
        weeks: (value.weeks as number) ?? 0,
        days: (value.days as number) ?? 0,
        hours: (value.hours as number) ?? 0,
        minutes: (value.minutes as number) ?? 0,
        seconds: (value.seconds as number) ?? 0,
        milliseconds: (value.milliseconds as number) ?? 0,
    };
}

// Duration tokens sorted longest-first.
// Uses same case convention as date/time: M = months, m = minutes.
const DURATION_TOKENS: Array<{
    token: string;
    resolve: (fields: DurationFields) => string;
}> = [
    { token: 'SSS', resolve: fields => pad(fields.milliseconds, 3) },
    { token: 'MM', resolve: fields => pad(fields.months, 2) },
    { token: 'HH', resolve: fields => pad(fields.hours, 2) },
    { token: 'mm', resolve: fields => pad(fields.minutes, 2) },
    { token: 'ss', resolve: fields => pad(fields.seconds, 2) },
    { token: 'Y', resolve: fields => String(fields.years) },
    { token: 'M', resolve: fields => String(fields.months) },
    { token: 'W', resolve: fields => String(fields.weeks) },
    { token: 'D', resolve: fields => String(fields.days) },
    { token: 'H', resolve: fields => String(fields.hours) },
    { token: 'm', resolve: fields => String(fields.minutes) },
    { token: 's', resolve: fields => String(fields.seconds) },
    { token: 'S', resolve: fields => String(fields.milliseconds) },
];

/**
 * Formats a duration plain object using a token-based pattern.
 * Literal text can be escaped with single quotes: `H'h' m'm'` -> `4h 5m`
 */
export function formatDurationPattern(value: Record<string, unknown>, pattern: string): string {
    const fields = extractDurationFields(value);
    let result = '';
    let index = 0;

    while (index < pattern.length) {
        // Handle escaped literal text
        if (pattern[index] === "'") {
            const closingQuote = pattern.indexOf("'", index + 1);
            if (closingQuote === -1) {
                result += pattern.slice(index + 1);
                break;
            }
            result += pattern.slice(index + 1, closingQuote);
            index = closingQuote + 1;
            continue;
        }

        // Try to match a token (longest first)
        let matched = false;
        for (const { token, resolve } of DURATION_TOKENS) {
            if (pattern.startsWith(token, index)) {
                result += resolve(fields);
                index += token.length;
                matched = true;
                break;
            }
        }

        if (!matched) {
            result += pattern[index];
            index++;
        }
    }

    return result;
}
